import React, { useEffect, useState } from 'react';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import { Box, Divider, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Button, IconButton } from '../../../../../components/ui';
import { useFeatureTheme } from '../../../../../theme/hooks/useFeatureTheme';
import { getAssignedGroupLabel, hasGroupAssignment } from '../../utils/fieldGroupUtils';
import FieldSettingsPanel from './FieldSettingsPanel';
import GroupManagementCallout from './GroupManagementCallout';

function GroupSection({ title, children, sx }) {
  const { semantic, radiusPx } = useFeatureTheme();

  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: radiusPx.md,
        border: `1px solid ${semantic.borders.border}`,
        bgcolor: semantic.surfaces.surface,
        ...sx,
      }}
    >
      {title ? (
        <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
          {title}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

function AssignedGroupChip({ label, onClear }) {
  const { semantic, radiusPx, typographyStyles } = useFeatureTheme();

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.75,
        borderRadius: radiusPx.md,
        border: `1px solid ${semantic.interactive.primary}`,
        bgcolor: semantic.interactive.primarySoft,
        color: semantic.interactive.primary,
        fontSize: typographyStyles.bodyText?.fontSize ?? 14,
        fontWeight: 600,
        maxWidth: '100%',
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontSize: 'inherit',
          fontWeight: 'inherit',
          color: 'inherit',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <Tooltip title="Remove from group">
        <span>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Remove from group"
            onClick={onClear}
            sx={{
              color: semantic.interactive.primary,
              minWidth: 32,
              minHeight: 32,
              '&:hover:not(:disabled)': {
                bgcolor: semantic.surfaces.surface,
              },
            }}
          >
            <CloseOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

export default function FieldGroupSettings({
  groupOptions = [],
  groupTitle = '',
  groupId = '',
  fieldError,
  onSelectGroup,
  onAddGroup,
  onClearAssignment,
  onManageGroups,
}) {
  const { semantic, typographyStyles } = useFeatureTheme();
  const [localError, setLocalError] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [isChanging, setIsChanging] = useState(false);

  const assignedLabel = getAssignedGroupLabel(groupOptions, groupId, groupTitle);
  const isAssigned = hasGroupAssignment(groupId, groupTitle);
  const showAssignmentForm = !isAssigned || isChanging;
  const helperText = fieldError || localError;
  const trimmedNewGroupName = newGroupName.trim();
  const hasExistingGroups = groupOptions.length > 0;

  useEffect(() => {
    if (!isAssigned) {
      setIsChanging(true);
      setNewGroupName('');
    }
  }, [isAssigned]);

  const handleCreateAndAssign = () => {
    const result = onAddGroup?.(trimmedNewGroupName);
    if (result?.error) {
      setLocalError(result.error);
      return;
    }
    setLocalError('');
    setNewGroupName('');
    setIsChanging(false);
  };

  const handleSelectGroup = (value) => {
    setLocalError('');
    onSelectGroup?.(value);
    if (value) {
      setIsChanging(false);
      setNewGroupName('');
    }
  };

  const handleClearAssignment = () => {
    setLocalError('');
    setNewGroupName('');
    setIsChanging(true);
    onClearAssignment?.();
  };

  const handleChangeGroup = () => {
    setLocalError('');
    setNewGroupName('');
    setIsChanging(true);
  };

  const introText = (() => {
    if (isAssigned && !isChanging) {
      return 'This field is grouped. Change the assignment below or manage all form groups globally.';
    }
    if (hasExistingGroups) {
      return 'Assign this field to a form group.';
    }
    return 'No groups exist yet. Create your first group to assign this field.';
  })();

  const createSectionTitle = hasExistingGroups ? 'Create new group' : 'Create your first group';

  return (
    <FieldSettingsPanel title="Group assignment">
      <Typography
        variant="body2"
        sx={{
          ...typographyStyles.caption,
          color: semantic.text.secondary,
          mb: 1.5,
          lineHeight: 1.45,
        }}
      >
        {introText}
      </Typography>

      {isAssigned && !isChanging ? (
        <Stack spacing={1.5}>
          <GroupSection title="Current assignment">
            <Typography
              variant="caption"
              sx={{ color: semantic.text.secondary, display: 'block', mb: 1 }}
            >
              This field belongs to
            </Typography>
            <AssignedGroupChip label={assignedLabel} onClear={handleClearAssignment} />
          </GroupSection>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleChangeGroup}
            icon={<SwapHorizOutlined sx={{ fontSize: 18 }} />}
            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          >
            Change group
          </Button>

          <GroupManagementCallout
            groupCount={groupOptions.length}
            onManageGroups={onManageGroups}
          />
        </Stack>
      ) : null}

      {showAssignmentForm ? (
        <Stack spacing={1.5}>
          {isAssigned && isChanging ? (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              Choose a different group below. Your current assignment stays until you pick a new
              one.
            </Typography>
          ) : null}

          {hasExistingGroups ? (
            <>
              <GroupSection title="Use existing group">
                <TextField
                  select
                  label="Select group"
                  size="small"
                  fullWidth
                  value={groupId ?? ''}
                  onChange={(event) => handleSelectGroup(event.target.value)}
                  helperText="Pick a group to assign this field."
                >
                  <MenuItem value="">Select group</MenuItem>
                  {groupOptions.map((group) => (
                    <MenuItem key={group.id} value={String(group.id)}>
                      {group.title}
                    </MenuItem>
                  ))}
                </TextField>
              </GroupSection>

              <Divider>
                <Typography variant="caption" color="text.secondary">
                  OR
                </Typography>
              </Divider>
            </>
          ) : null}

          <GroupSection title={createSectionTitle}>
            <Stack spacing={1.5}>
              <TextField
                label="Group name"
                size="small"
                fullWidth
                value={newGroupName}
                onChange={(event) => {
                  setLocalError('');
                  setNewGroupName(event.target.value);
                }}
                error={Boolean(helperText)}
                helperText={helperText || 'Creates a new group and assigns this field to it.'}
                placeholder="Enter a group name"
              />

              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={!trimmedNewGroupName}
                onClick={handleCreateAndAssign}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
              >
                Create & assign
              </Button>
            </Stack>
          </GroupSection>

          {hasExistingGroups ? (
            <GroupManagementCallout
              groupCount={groupOptions.length}
              onManageGroups={onManageGroups}
            />
          ) : null}

          {isAssigned && isChanging ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setLocalError('');
                setNewGroupName('');
                setIsChanging(false);
              }}
              sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
            >
              Cancel change
            </Button>
          ) : null}
        </Stack>
      ) : null}

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 1.5,
          color: semantic.text.secondary,
          lineHeight: 1.45,
        }}
      >
        Turn off Group field above to disable grouping for this field.
      </Typography>
    </FieldSettingsPanel>
  );
}
